import type { HandAttachPose } from './ProcArms';
import { bakedAttachPose } from './ProcArms';
import type { ReloadXform } from './DoubleReloadClip';
import type { WeaponId } from '../weapons/WeaponDefs';

export type MagReloadWeaponId = 'pistol' | 'mp5' | 'ak47';

export function isMagReloadWeapon(id: WeaponId): id is MagReloadWeaponId {
  return id === 'pistol' || id === 'mp5' || id === 'ak47';
}

export type PistolReloadBeatId = 'hip' | 'away' | 'drop' | 'fetch' | 'seat' | 'home';
export type MagReloadBeatId = PistolReloadBeatId;
export type MagReloadBeat = PistolReloadBeat;

export interface PistolReloadBeat {
  id: PistolReloadBeatId;
  label: string;
  /** 0..1 of reloadTime. */
  t: number;
  gun: ReloadXform;
  /** 0 = left hand on grip_l, 1 = dummy. */
  dummyBlend: number;
  /**
   * 1 = dummy sits on grip_l (hip hand orientation, xyz is a pose offset).
   * 0 = dummy uses its own pitch/yaw/roll in pose space.
   * Keep this at 1 for hip/away/drop so the leave does not spin.
   */
  followGrip: number;
  dummy: ReloadXform;
  hand: HandAttachPose;
  /** Held mag pose in dummy space (only drawn when magHold). */
  held: ReloadXform;
  /** Fresh mag in the left hand. */
  magHold: number;
  /** Mag seated in the gun. */
  magSeated: number;
}

const ZERO: ReloadXform = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };
const PISTOL_LIFT: ReloadXform = { x: 0.02, y: 0.045, z: 0.025, pitch: 0.28, yaw: 0.1, roll: 0.05 };
const RIFLE_LIFT: ReloadXform = { x: 0.015, y: 0.03, z: 0.02, pitch: 0.18, yaw: 0.08, roll: 0.04 };
/** Mag sitting in the dummy palm. Tune per-beat with the Mag sliders. */
const MAG_PALM: ReloadXform = { x: 0.002, y: -0.012, z: -0.028, pitch: 0.15, yaw: 0, roll: 0 };

function hipL(id: MagReloadWeaponId): HandAttachPose {
  return bakedAttachPose(id, 'L');
}

function xform(src: ReloadXform): ReloadXform {
  return { ...src };
}

function beat(
  id: PistolReloadBeatId,
  label: string,
  t: number,
  extra: Omit<PistolReloadBeat, 'id' | 'label' | 't'>,
): PistolReloadBeat {
  return {
    id,
    label,
    t,
    gun: xform(extra.gun),
    dummyBlend: extra.dummyBlend,
    followGrip: extra.followGrip,
    dummy: xform(extra.dummy),
    hand: { ...extra.hand },
    held: xform(extra.held),
    magHold: extra.magHold,
    magSeated: extra.magSeated,
  };
}

export const PISTOL_RELOAD_BEAT_ORDER: PistolReloadBeatId[] = [
  'hip',
  'away',
  'drop',
  'fetch',
  'seat',
  'home',
];
export const MAG_RELOAD_BEAT_ORDER = PISTOL_RELOAD_BEAT_ORDER;

export function bakedPistolReloadClip(): PistolReloadBeat[] {
  const L = hipL('pistol');
  return [
    beat('hip', 'Hip', 0, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
    beat('away', 'Away', 0.12, {
      gun: PISTOL_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.082, y: -0.228, z: -0.036, pitch: 0, yaw: 0, roll: 0 },
      hand: L,
      held: { x: -0.13, y: -0.012, z: -0.028, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 0,
      magSeated: 1,
    }),
    beat('drop', 'Drop', 0.22, {
      gun: PISTOL_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.22, y: -0.1, z: -0.055, pitch: 0, yaw: 0, roll: 0 },
      hand: { ...L, x: -0.125, y: 0.22, z: 0.198 },
      held: { x: -0.125, y: -0.012, z: -0.028, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 0,
      magSeated: 0,
    }),
    beat('fetch', 'Fetch', 0.48, {
      gun: PISTOL_LIFT,
      dummyBlend: 1,
      followGrip: 0.8,
      dummy: { x: -0.14, y: -0.137, z: 0.05, pitch: 0, yaw: -0.04, roll: 0 },
      hand: {
        x: -0.095,
        y: 0.012,
        z: 0.02,
        rx: -1.65,
        ry: 0.18,
        rz: 1.43,
        wx: 0,
        wy: 0,
        wz: -0.07,
        scale: 1.28,
        curl: 0.06,
        knuckle: -0.29,
      },
      held: { x: -0.08, y: -0.036, z: 0.032, pitch: -0.47, yaw: 0.18, roll: -1.58 },
      magHold: 1,
      magSeated: 0,
    }),
    beat('seat', 'Seat', 0.72, {
      gun: { x: 0.01, y: 0.03, z: 0.02, pitch: 0.18, yaw: 0.06, roll: 0.03 },
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.091, y: -0.137, z: 0.03, pitch: 0, yaw: 0, roll: 0 },
      hand: {
        x: 0.018,
        y: -0.138,
        z: 0.025,
        rx: -1.58,
        ry: 0.18,
        rz: 1.43,
        wx: 0,
        wy: 0,
        wz: 0,
        scale: 1.28,
        curl: 0.35,
        knuckle: 0.12,
      },
      held: { x: 0.059, y: -0.118, z: 0.066, pitch: -0.75, yaw: -0.22, roll: -1.36 },
      magHold: 1,
      magSeated: 0.25,
    }),
    beat('home', 'Home', 1, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
  ];
}

function magHand(
  x: number,
  y: number,
  z: number,
  rx: number,
  ry: number,
  rz: number,
  wx: number,
  wy: number,
  wz: number,
  scale: number,
  curl: number,
  knuckle: number,
): HandAttachPose {
  return { x, y, z, rx, ry, rz, wx, wy, wz, scale, curl, knuckle };
}

export function bakedMp5ReloadClip(): PistolReloadBeat[] {
  const L = hipL('mp5');
  return [
    beat('hip', 'Hip', 0, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
    beat('away', 'Away', 0.12, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.04, y: -0.07, z: 0.09, pitch: 0, yaw: 0, roll: 0 },
      hand: magHand(-0.043, -0.115, 0.018, -0.11, -0.25, -1.54, 0.9, -0.57, -2.44, 1.3, -0.63, 0),
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
    beat('drop', 'Drop', 0.22, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.06, y: -0.13, z: 0.07, pitch: 0, yaw: 0, roll: 0 },
      hand: magHand(0.007, -0.035, 0.007, 0.04, -0.29, -0.9, 1.15, -0.57, -2.09, 1.28, 0.22, 0.08),
      held: { x: 0.057, y: 0.096, z: 0.02, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 1,
      magSeated: 0,
    }),
    beat('fetch', 'Fetch', 0.48, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 0.7,
      dummy: { x: -0.1, y: -0.15, z: 0.05, pitch: 0.15, yaw: -0.08, roll: 0.04 },
      hand: magHand(-0.073, -0.17, 0.007, 0.04, -0.29, -0.9, 1.15, -0.57, -2.09, 1.28, 0.22, 0.08),
      held: { x: -0.034, y: -0.018, z: 0.041, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 1,
      magSeated: 0,
    }),
    beat('seat', 'Seat', 0.72, {
      gun: { x: 0.01, y: 0.022, z: 0.016, pitch: 0.12, yaw: 0.05, roll: 0.03 },
      dummyBlend: 1,
      followGrip: 0.85,
      dummy: { x: -0.02, y: -0.09, z: 0.054, pitch: 0.08, yaw: 0, roll: 0 },
      hand: magHand(-0.038, -0.068, 0.025, 0.04, -0.29, -0.9, 1.15, -0.57, -2.09, 1.28, 0.22, 0.08),
      held: { x: 0.002, y: 0.08, z: 0.023, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 1,
      magSeated: 0.25,
    }),
    beat('home', 'Home', 1, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
  ];
}

export function bakedAk47ReloadClip(): PistolReloadBeat[] {
  const L = hipL('ak47');
  return [
    beat('hip', 'Hip', 0, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
    beat('away', 'Away', 0.12, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.05, y: -0.08, z: 0.1, pitch: 0, yaw: 0, roll: 0 },
      hand: magHand(0.01, -0.068, 0.033, 0, -0.61, -0.9, 0.47, 0.07, -2.4, 1.3, -0.18, -0.11),
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
    beat('drop', 'Drop', 0.22, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 1,
      dummy: { x: -0.014, y: -0.114, z: 0.08, pitch: 0, yaw: 0, roll: 0 },
      hand: magHand(-0.033, -0.025, 0.033, 0, -0.29, -0.9, 0.72, 0.07, -2.05, 1.28, -0.05, 0.08),
      held: { x: 0.005, y: 0.114, z: 0.041, pitch: 0, yaw: 0.04, roll: -0.04 },
      magHold: 1,
      magSeated: 0,
    }),
    beat('fetch', 'Fetch', 0.48, {
      gun: RIFLE_LIFT,
      dummyBlend: 1,
      followGrip: 0.7,
      dummy: { x: -0.095, y: -0.269, z: 0.06, pitch: 0.15, yaw: -0.08, roll: 0.04 },
      hand: magHand(0.03, -0.09, 0.033, 0, -0.29, -0.9, 0.72, 0.07, -2.05, 1.28, 0.22, 0.08),
      held: { x: 0.059, y: 0.068, z: 0.061, pitch: 0.15, yaw: 0, roll: 0 },
      magHold: 1,
      magSeated: 0,
    }),
    beat('seat', 'Seat', 0.72, {
      gun: { x: 0.01, y: 0.022, z: 0.016, pitch: 0.12, yaw: 0.05, roll: 0.03 },
      dummyBlend: 1,
      followGrip: 0.85,
      dummy: { x: 0, y: 0, z: 0, pitch: 0.08, yaw: 0, roll: 0 },
      hand: magHand(-0.045, -0.15, 0.085, -0.04, -0.29, -0.9, 0.72, 0.07, -2.12, 1.28, 0.22, 0.08),
      held: { x: 0.009, y: 0.016, z: 0.1, pitch: 0.15, yaw: 0.14, roll: 0 },
      magHold: 1,
      magSeated: 0.26,
    }),
    beat('home', 'Home', 1, {
      gun: ZERO,
      dummyBlend: 0,
      followGrip: 1,
      dummy: ZERO,
      hand: L,
      held: MAG_PALM,
      magHold: 0,
      magSeated: 1,
    }),
  ];
}

export function bakedMagReloadClip(id: MagReloadWeaponId): PistolReloadBeat[] {
  if (id === 'mp5') return bakedMp5ReloadClip();
  if (id === 'ak47') return bakedAk47ReloadClip();
  return bakedPistolReloadClip();
}

export function clonePistolReloadClip(src: PistolReloadBeat[] = bakedPistolReloadClip()): PistolReloadBeat[] {
  return src.map((b) => beat(b.id, b.label, b.t, b));
}

export function cloneMagReloadClip(id: MagReloadWeaponId): PistolReloadBeat[] {
  return clonePistolReloadClip(bakedMagReloadClip(id));
}

export function copyPistolReloadBeat(src: PistolReloadBeat, dst: PistolReloadBeat): void {
  dst.id = src.id;
  dst.label = src.label;
  dst.t = src.t;
  Object.assign(dst.gun, src.gun);
  dst.dummyBlend = src.dummyBlend;
  dst.followGrip = src.followGrip;
  Object.assign(dst.dummy, src.dummy);
  Object.assign(dst.hand, src.hand);
  Object.assign(dst.held, src.held);
  dst.magHold = src.magHold;
  dst.magSeated = src.magSeated;
}

export function emptyPistolReloadSample(): PistolReloadBeat {
  return beat('hip', 'Hip', 0, {
    gun: ZERO,
    dummyBlend: 0,
    followGrip: 1,
    dummy: ZERO,
    hand: hipL('pistol'),
    held: MAG_PALM,
    magHold: 0,
    magSeated: 1,
  });
}

function lerpNum(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpXform(a: ReloadXform, b: ReloadXform, t: number, out: ReloadXform): void {
  out.x = lerpNum(a.x, b.x, t);
  out.y = lerpNum(a.y, b.y, t);
  out.z = lerpNum(a.z, b.z, t);
  out.pitch = lerpNum(a.pitch, b.pitch, t);
  out.yaw = lerpNum(a.yaw, b.yaw, t);
  out.roll = lerpNum(a.roll, b.roll, t);
}

function lerpHand(a: HandAttachPose, b: HandAttachPose, t: number, out: HandAttachPose): void {
  out.x = lerpNum(a.x, b.x, t);
  out.y = lerpNum(a.y, b.y, t);
  out.z = lerpNum(a.z, b.z, t);
  out.rx = lerpNum(a.rx, b.rx, t);
  out.ry = lerpNum(a.ry, b.ry, t);
  out.rz = lerpNum(a.rz, b.rz, t);
  out.wx = lerpNum(a.wx, b.wx, t);
  out.wy = lerpNum(a.wy, b.wy, t);
  out.wz = lerpNum(a.wz, b.wz, t);
  out.scale = lerpNum(a.scale, b.scale, t);
  out.curl = lerpNum(a.curl, b.curl, t);
  out.knuckle = lerpNum(a.knuckle, b.knuckle, t);
}

function smooth01(t: number): number {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return u * u * (3 - 2 * u);
}

export function samplePistolReloadClip(
  clip: PistolReloadBeat[],
  t: number,
  out: PistolReloadBeat,
): PistolReloadBeat {
  const n = clip.length;
  if (n === 0) return out;
  const first = clip[0]!;
  const last = clip[n - 1]!;
  if (t <= first.t) {
    copyPistolReloadBeat(first, out);
    return out;
  }
  if (t >= last.t) {
    copyPistolReloadBeat(last, out);
    return out;
  }
  let i = 0;
  while (i < n - 1 && clip[i + 1]!.t < t) i += 1;
  const a = clip[i]!;
  const b = clip[i + 1]!;
  const span = b.t - a.t;
  const u = span <= 1e-6 ? 1 : smooth01((t - a.t) / span);
  out.id = u < 1 ? a.id : b.id;
  out.label = u < 1 ? a.label : b.label;
  out.t = t;
  lerpXform(a.gun, b.gun, u, out.gun);
  out.dummyBlend = lerpNum(a.dummyBlend, b.dummyBlend, u);
  out.followGrip = lerpNum(a.followGrip, b.followGrip, u);
  lerpXform(a.dummy, b.dummy, u, out.dummy);
  lerpHand(a.hand, b.hand, u, out.hand);
  lerpXform(a.held, b.held, u, out.held);
  out.magHold = lerpNum(a.magHold, b.magHold, u);
  out.magSeated = lerpNum(a.magSeated, b.magSeated, u);
  return out;
}
